import { apiRequest } from '@/shared/api/client';
import type { Envelope } from '@/shared/types/api';

import type {
  CommercialHistoryEvent,
  OrganisationDetail,
  OrganisationSummary,
  PlatformOverview,
  PlatformSessionResponse,
  ProvisionInput,
  ProvisionResult,
  PlatformPage,
  PlatformFeature,
  CommercialPlan,
  PlanInput,
  PlanVersion,
  PlanVersionInput,
  PlatformSubscription,
  PlatformSubscriptionDetail,
  SubscriptionChange,
} from './types';

/*
 * The platform API, under its own prefix and guard. Nothing here calls a
 * tenant or supplier endpoint, and nothing a tenant screen uses calls these.
 */

export interface OrganisationPage {
  data: OrganisationSummary[];
  meta: { current_page: number; last_page: number; per_page: number; total: number };
}

export const platformMe = () => apiRequest<Envelope<PlatformSessionResponse>>('/platform/auth/me');

export const platformLogin = (credentials: { email: string; password: string }) =>
  apiRequest<Envelope<PlatformSessionResponse>>('/platform/auth/login', {
    method: 'POST',
    body: credentials,
  });

export const platformLogout = () => apiRequest<void>('/platform/auth/logout', { method: 'POST' });

export const platformOverview = () => apiRequest<Envelope<PlatformOverview>>('/platform/overview');

export const listOrganisations = (params: {
  search?: string;
  status?: string;
  page?: number;
  administrator?: string;
  plan_id?: string;
  subscription_status?: string;
  commercial_context?: string;
  readiness?: string;
  per_page?: number;
}) =>
  apiRequest<OrganisationPage>('/platform/tenants', {
    query: {
      search: params.search || undefined,
      status: params.status || undefined,
      page: params.page,
      administrator: params.administrator || undefined,
      plan_id: params.plan_id || undefined,
      subscription_status: params.subscription_status || undefined,
      commercial_context: params.commercial_context || undefined,
      readiness: params.readiness || undefined,
      per_page: params.per_page,
    },
  });

export const showOrganisation = (id: string, signal?: AbortSignal) =>
  apiRequest<Envelope<OrganisationDetail>>(`/platform/tenants/${id}`, { signal });

/**
 * One page of an organisation's commercial history, newest first. The server
 * pages by keyset (PlatformCommercialHistoryController): `next_cursor` is an
 * opaque, URL-safe string to send back verbatim as `cursor` for the page of
 * older events (never read, parsed or rebuilt here), and null on the last page.
 */
export interface CommercialHistoryPage {
  data: CommercialHistoryEvent[];
  meta: { has_more: boolean; next_cursor?: string | null };
}

export const organisationHistory = (id: string, cursor?: string | null) =>
  apiRequest<CommercialHistoryPage>('/platform/commercial-history', {
    query: { tenant_id: id, cursor: cursor ?? undefined },
  });

export const provisionOrganisation = (input: ProvisionInput, idempotencyKey: string) =>
  apiRequest<Envelope<ProvisionResult>>('/platform/tenants', {
    method: 'POST',
    body: input,
    idempotencyKey,
  });

export const resendAdministratorInvitation = (id: string) =>
  apiRequest<Envelope<unknown>>(`/platform/tenants/${id}/administrator/invitation`, {
    method: 'POST',
  });

export const listPlans = (
  query: { search?: string; status?: string; page?: number; per_page?: number } = {},
) => apiRequest<PlatformPage<CommercialPlan>>('/platform/plans', { query });
export const showPlan = (id: string, signal?: AbortSignal) =>
  apiRequest<Envelope<CommercialPlan>>(`/platform/plans/${id}`, { signal });
export const savePlan = (input: PlanInput, id?: string) =>
  apiRequest<Envelope<CommercialPlan>>(id ? `/platform/plans/${id}` : '/platform/plans', {
    method: id ? 'PUT' : 'POST',
    body: input,
  });
export const planState = (id: string, action: 'retire' | 'activate', reason: string) =>
  apiRequest<Envelope<CommercialPlan>>(`/platform/plans/${id}/${action}`, {
    method: 'POST',
    body: { reason },
  });
export const savePlanVersion = (plan: string, input: PlanVersionInput, version?: string) =>
  apiRequest<Envelope<PlanVersion>>(
    version ? `/platform/plan-versions/${version}` : `/platform/plans/${plan}/versions`,
    { method: version ? 'PUT' : 'POST', body: input },
  );
export const publishPlanVersion = (id: string) =>
  apiRequest<Envelope<PlanVersion>>(`/platform/plan-versions/${id}/publish`, { method: 'POST' });
export const listPlatformFeatures = () =>
  apiRequest<Envelope<PlatformFeature[]>>('/platform/features');
export const listSubscriptions = (
  query: {
    search?: string;
    status?: string;
    tenant_id?: string;
    plan_id?: string;
    page?: number;
    per_page?: number;
  } = {},
) => apiRequest<PlatformPage<PlatformSubscription>>('/platform/subscriptions', { query });
export const showSubscription = (id: string) =>
  apiRequest<Envelope<PlatformSubscriptionDetail>>(`/platform/subscriptions/${id}`);
export const tenantSubscription = (id: string) =>
  apiRequest<Envelope<PlatformSubscriptionDetail | null>>(`/platform/tenants/${id}/subscription`);
export const assignSubscription = (
  tenant: string,
  input: {
    plan_version_id: string;
    contract_id: string;
    starts_at: string;
    ends_at: string;
    reason: string;
  },
) =>
  apiRequest<Envelope<PlatformSubscriptionDetail>>(`/platform/tenants/${tenant}/subscriptions`, {
    method: 'POST',
    body: input,
  });
export const subscriptionState = (id: string, action: 'suspend' | 'reinstate', reason: string) =>
  apiRequest<Envelope<PlatformSubscriptionDetail>>(`/platform/subscriptions/${id}/${action}`, {
    method: 'POST',
    body: { reason },
  });
export const listSubscriptionChanges = (tenant: string) =>
  apiRequest<{ data: SubscriptionChange[]; meta: { total: number; held: number } }>(
    '/platform/subscription-changes',
    { query: { tenant_id: tenant } },
  );
export const proposeSubscriptionChange = (input: {
  subscription_id: string;
  plan_code?: string;
  feature?: string;
  target_limit?: number | null;
  reason: string;
}) =>
  apiRequest<Envelope<SubscriptionChange>>('/platform/subscription-changes', {
    method: 'POST',
    body: input,
  });
export const decideSubscriptionChange = (
  id: string,
  action: 'approve' | 'apply' | 'cancel',
  reason: string,
) =>
  apiRequest<Envelope<SubscriptionChange>>(`/platform/subscription-changes/${id}/${action}`, {
    method: 'POST',
    body: { reason },
  });

export const organisationState = (id: string, action: 'suspend' | 'reinstate', reason: string) =>
  apiRequest<Envelope<{ id: string; status: string; status_label: string }>>(
    `/platform/tenants/${id}/${action}`,
    { method: 'POST', body: { reason } },
  );
